import {uniqBy} from "lodash";
import "../css/select.css";
import "../css/searchbox.css";
import React, {ClipboardEvent, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Button, OverlayTrigger, Tooltip} from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import {BiSearch} from "react-icons/bi";
import {useSearchParams} from "react-router-dom";
import {components, GroupBase, MultiValue, OptionProps, SingleValue} from "react-select";
import AsyncSelect from "react-select/async";
import {useTheCellMapContext} from "../contexts/TheCellMapContext";
import {useEntrezMap} from "../hooks/useEntrezMap";
import {useFileDataset, useFileGenes} from "../hooks/useJsonLoader";
import {AppSettings} from "../settings";
import {URLParam} from "../types/enums";
import {SearchOption} from "../types/Types";
import {optionsRemoveDisabled, optionsToQueryString, renderOption} from "../utils/option";
import {useTrackSearch} from "../utils/tracker";

const SELECT_ALL_VALUE = Number.MIN_SAFE_INTEGER;
const SELECT_ALL_MAX_RESULTS = 100;


type SearchBoxProps = {
  isMulti?: boolean;  // Allow multiple selection, default true
  lastAdded?: (option: SearchOption) => void | undefined; // Highlight last added option (not implemented yet)
  mobileInline?: boolean; // Render the full input on mobile instead of the search-modal button
}

const SearchBox = React.memo(({isMulti = true, lastAdded = undefined, mobileInline = false}: SearchBoxProps) => {
  const {selectedGenes, setSelectedGenes} = useTheCellMapContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const dataset = useFileDataset();
  const genes = useFileGenes();
  const entrezMap = useEntrezMap();
  const trackSearch = useTrackSearch();

  const [isReady, setIsReady] = useState(false);
  const [show, setShow] = useState(false);
  const lastSearchString = useRef("");

  const selectedEntrez = useMemo(() =>
      selectedGenes.map(g => g.value),
    [selectedGenes]);

  /**
   * Indexed nodes and genes by entrez ID.
   */
  const searchableGenes = useMemo(() => {
    if (!dataset || !genes || !entrezMap) return undefined;

    const nodeEntrez = Object.fromEntries(dataset.nodes.map(node => [node.e, node]));

    return Object.fromEntries(genes.map(gene => {
      const node = nodeEntrez[gene.e];

      return [gene.e, {
        gene,
        node,
        symbolLower: gene.s.toLowerCase(),
        nameLower: node ? node.n.toLowerCase() : '',
        aliasesLower: (gene.a ?? []).map(a => a.toLowerCase()),
      }];
    }));
  }, [dataset, genes, entrezMap]);

  /**
   * Search tracking
   */
  useEffect(() => {
    if (selectedGenes.length === 0) return;

    const timer = setTimeout(() => {
      trackSearch(selectedGenes.map(gene => gene.searched).join(', '), selectedGenes.length);
    }, 3000); // wait 1.5s after last change

    return () => clearTimeout(timer);
  }, [selectedGenes]);

  /**
   * Update the URL search params when the selected genes change.
   */
  useEffect(() => {
    /*
      FIXME: If we really insist on esthetics (commas and colons), reimplement parameter setting.
       But make sure all parameters are preserved, not just "g". Best would be to make wrapper around searchParams.
       Idea:

      const query = optionsToQueryString(state.selectedGenes);
      const url = `${window.location.pathname}?g=${query}`;
      window.history.replaceState(null, "", url);

      // Force React to notice the change
      setSearchParams(new URLSearchParams(window.location.search));
     */

    if (!isReady) return;

    const query = optionsToQueryString(selectedGenes);
    const newParams = new URLSearchParams(searchParams);

    if (query.length === 0) {
      newParams.delete(URLParam.GENES);
    } else {
      newParams.set(URLParam.GENES, query);
    }

    if (newParams.toString() !== searchParams.toString()) {
      setSearchParams(newParams, {replace: true});
    }
  }, [selectedGenes, searchParams, setSearchParams, isReady]);

  /**
   * Filters the searchable genes based on the input value. Construct labels and defines isNode flag.
   */
  const filterOptions = useCallback(
    (inputValue: string, maxResults = 100, addSelectAll = true): SearchOption[] => {
      if (!searchableGenes) return [];

      const searchString = inputValue.toLowerCase();
      const results: SearchOption[] = [];
      // Use exact matching for short queries
      const exactMatch = searchString.length < AppSettings.MIN_SEARCH_LEN;

      for (const n of Object.values(searchableGenes)) {
        if (results.length >= maxResults) break;

        const {gene, node, symbolLower, nameLower, aliasesLower} = n;

        if (selectedEntrez.includes(gene.e)) continue; // Skip already selected, in case of single-select

        /* If the search term is a number then the user must be looking for EntrezID */
        const searchNumber = Number(searchString);
        if (!isNaN(searchNumber) && searchNumber === gene.e) { // Entrez ID full match
          results.push({value: gene.e, searched: `NCBI:${gene.e.toString()}`, symbol: gene.s, node: node, gene});
          continue;
        }

        if (exactMatch ? symbolLower === searchString : symbolLower.includes(searchString)) { // Prioritize symbol match
          results.push({value: gene.e, searched: gene.s, symbol: gene.s, node: node, gene});
          continue;
        }

        if (exactMatch ? nameLower === searchString : nameLower.includes(searchString)) { // Node name match
          results.push({value: gene.e, searched: node.n, symbol: gene.s, node: node, gene});
          continue;
        }

        const aliasIndex = aliasesLower.findIndex(a => exactMatch ? a === searchString : a.includes(searchString)); // Alias match
        if (aliasIndex !== -1) {
          const aliasOriginal = gene.a[aliasIndex];
          results.push({value: gene.e, searched: aliasOriginal, symbol: gene.s, node: node, gene});
        }
      }

      // Sort by isNode first, then alphabetically by symbol
      const sorted = results.sort((a, b) => {
        if (!!a.node && !b.node) return -1; // a first
        if (!a.node && !!b.node) return 1;  // b first
        return a.symbol.localeCompare(b.symbol);
      });

      // NOTE: Alternatively, sort just alphabetically by symbol
      // results.sort((a, b) => a.symbol.localeCompare(b.symbol));

      const numNonDisabled = sorted.filter(o => !o.gene?.d).length;

      if (addSelectAll && isMulti && numNonDisabled > 1 && numNonDisabled <= SELECT_ALL_MAX_RESULTS) {
        const label = `Select all ${numNonDisabled} available matched symbols`;
        sorted.push({value: SELECT_ALL_VALUE, searched: label, symbol: label, node: undefined, gene: undefined});
      }

      return sorted;
    },
    [searchableGenes, selectedEntrez]
  );

  /**
   * Load the selected genes from the URL search params. Only happens once when component mounts.
   */
  useEffect(() => {
    if (!searchableGenes || isReady) return;

    const selected: SearchOption[] = [];
    const queries = decodeURIComponent(searchParams.get(URLParam.GENES) ?? "").split(",");

    /*
    TODO: Allow direct link by just entrez id, in case somebody like alliance wants to share a link to a specific gene
     */

    for (const q of queries) {
      if (!q) continue;

      const [searched, entrezStr] = q.split("::");
      const entrez = entrezStr ? parseInt(entrezStr, 10) : undefined;

      if (!entrez) {
        selected.push(...filterOptions(searched));
      } else {
        const n = searchableGenes[entrez];
        if (!n) continue;
        const {gene, node} = n;
        selected.push({value: gene.e, searched, symbol: gene.s, node: node, gene});
      }
    }

    if (selected.length > 0) {
      // Remove any disabled genes from the selection
      setSelectedGenes(optionsRemoveDisabled(selected));
    }

    setIsReady(true);
  }, [searchableGenes, searchParams, filterOptions, setSelectedGenes, isReady, setIsReady]);

  /**
   * Disable/hide the DropdownIndicator component on the right side of our search box
   * @constructor
   */
  const DropdownIndicator = () => null;

  /**
   * Custom select input so we can catch paste events. Also makes sure the input is in focus on load
   * @constructor
   */
  const CustomInput = ({innerRef, ...props}: any) => {
    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
      inputRef.current?.focus();
    }, []);

    return <components.Input {...props} onPaste={onPaste} innerRef={(node) => {
      inputRef.current = node;
      if (typeof innerRef === 'function') innerRef(node);
      else if (innerRef) innerRef.current = node;
    }}/>;
  };

  const Option = (props: OptionProps<SearchOption, boolean, GroupBase<SearchOption>>) => {
    const {isDisabled} = props;

    const renderTooltip = (tooltipProps: any) =>
      <Tooltip id="option-tooltip" className="white-tooltip" {...tooltipProps}>
        Interactions do not satisfy the standard confidence threshold
      </Tooltip>;

    const content = <div>{props.children}</div>;

    return (
      <components.Option {...props}>
        {isDisabled ? (<OverlayTrigger
          placement="right"
          delay={{show: 50, hide: 100}}
          overlay={renderTooltip}
        >
          {content}
        </OverlayTrigger>) : (
          content
        )}
      </components.Option>
    );
  };

  /**
   * Alert user how many characters are needed before search is triggered and let them know if no results are found
   */
  const noOptionsMsg = ({inputValue}: { inputValue: string }) => {
    if (inputValue.length === 0) return "Start typing";
    if (inputValue.length < AppSettings.MIN_SEARCH_LEN) return "No exact match. Enter at least 3 characters to search.";

    return "No symbols found";
  };

  /**
   * Filters genes asynchronously for our search box.
   *
   * Calls the callback with matching options after a short delay.
   * If the input length is less than MIN_SEARCH_LEN, returns an empty array immediately.
   *
   * @param inputValue The current search input string.
   * @param callback Function to receive the filtered options.
   */
  const loadOptions = useCallback((inputValue: string): Promise<SearchOption[]> => {
    lastSearchString.current = inputValue;
    return Promise.resolve(filterOptions(inputValue));
  }, [filterOptions]);

  /**
   * Handle paste into search box. Tokenize text and pass items on to select to handle search
   * @param event
   */
  const onPaste = (event: ClipboardEvent) => {
    const paste = event.clipboardData?.getData("text/plain") ?? "";
    let pastedItems = paste.split(/\s+/).filter((p) => p.trim().length > 0);

    pastedItems = uniqBy(pastedItems, item => item);

    // We have valid options
    if (pastedItems.length > 1) {
      event.preventDefault(); // Stop normal paste behaviour

      const pastedSelected: SearchOption[] = optionsRemoveDisabled(pastedItems.flatMap(item => filterOptions(item)));

      // Add pasted options to existing selection, remove duplicates if they exist
      setSelectedGenes(uniqBy([...selectedGenes, ...pastedSelected], opt => opt.value));
    }
  };

  /**
   * Handle selection changes for both multiselect or single select modes
   */
  const onChange = (newValue: SingleValue<SearchOption> | MultiValue<SearchOption>) => {
    if (isMulti) {
      let newList = newValue as SearchOption[];
      const hasSelectAll = newList.some(o => o.value === SELECT_ALL_VALUE);

      if (hasSelectAll) {
        // Remove meta select all option
        newList = newList.filter(o => o.value !== SELECT_ALL_VALUE);
        // Add all displayed options
        newList = [...newList, ...filterOptions(lastSearchString.current, SELECT_ALL_MAX_RESULTS, false)];
        // Remove disabled nodes if there are any
        newList = optionsRemoveDisabled(newList);
      }

      setSelectedGenes(newList);
      if (lastAdded && newList.length > 0) lastAdded(newList[newList.length - 1]);
    } else if (newValue) {
      const newList = [...selectedGenes, newValue as SearchOption];
      setSelectedGenes(newList);
      if (lastAdded) lastAdded(newValue as SearchOption);
    }
  }

  const select = (<AsyncSelect<SearchOption, boolean>
    isMulti={isMulti}
    value={isReady && isMulti ? selectedGenes : null}  // Update only when ready and in multi mode
    name={"search_bar"}
    id={"search-select"}
    className={"basic-multi-select"}
    classNamePrefix={"select"}
    blurInputOnSelect={false}
    autoFocus={true}
    placeholder={"Search genes (symbol / EntrezID)"}
    components={{DropdownIndicator, Option, Input: CustomInput}}
    loadOptions={loadOptions}
    noOptionsMessage={noOptionsMsg}
    formatOptionLabel={renderOption}
    onChange={onChange}
    isOptionDisabled={option => !!option.gene?.d}
    styles={{
      option: (base, state) => ({
        ...base,
        color: state.isDisabled ? '#888' : base.color,
        opacity: state.isDisabled ? 1 : base.opacity,
      }),
      placeholder: (base) => ({
        ...base,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      })
    }}
  />);

  if (mobileInline) return select;

  return <>
    <div className="d-none d-md-block">{select}</div>

    <div className="d-block d-md-none">
      <Button variant="outline-light" className="w-100" onClick={() => setShow(true)}>
        <BiSearch className="ui-icon"/>
      </Button>

      <Modal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Search Genes</Modal.Title>
        </Modal.Header>
        <Modal.Body>{select}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShow(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  </>
});

export default SearchBox;
